import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { FormComponent, UiComponent } from 'components';
import { PushQuery } from 'queries';
import { NOTI_MENU_URL } from 'data/menu';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import useModal from 'hooks/useModal';
import {
  EventItemType,
  IContent,
  IPushMessage,
  IUserGroupCandidate,
  PushDestinationType,
  PushTargetType,
  PushType,
} from 'types';
import ContentSelect from '../ContentSelect';
import EventSelect from '../EventSelect';
import Preview from '../Preview';
import UserSelect from '../UserSelect';
import {
  PUSH_BODY_MAX_LENGTH,
  DESTINATIONS_WITH_DETAIL,
  PUSH_DESTINATION_OPTIONS,
  PUSH_TARGET_OPTIONS,
  PUSH_TITLE_MAX_LENGTH,
  PUSH_TYPE_OPTIONS,
  SCHEDULE_MINUTE_UNIT,
} from '../constants';
import * as styles from './styles';

type SendMethod = 'NOW' | 'SCHEDULE';

interface Props {
  /** 수정 진입 시 기존 값 */
  push?: IPushMessage;
}

const getErrorMessage = (error: unknown) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? DEFAULT_ERROR_MESSAGE;

function Form({ push }: Props) {
  const navigate = useNavigate();
  const { handleConfirm } = useModal();
  const pushUrl = NOTI_MENU_URL['앱 푸시 관리'];

  const [pushType, setPushType] = useState<PushType>('NOTICE');
  const [targetType, setTargetType] = useState<PushTargetType>('ALL');
  const [selectedUsers, setSelectedUsers] = useState<IUserGroupCandidate[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [destinationType, setDestinationType] =
    useState<PushDestinationType>('TAB_HOME');
  const [selectedEvent, setSelectedEvent] = useState<EventItemType | null>(
    null,
  );
  const [destinationDetail, setDestinationDetail] = useState<string | null>(
    null,
  );
  const [sendMethod, setSendMethod] = useState<SendMethod>('NOW');
  const [sendAt, setSendAt] = useState<Date | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<IContent | null>(null);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const { data: targetCount } = PushQuery.useGetTargetCount();
  const { mutateAsync: create, isPending: isCreating } = PushQuery.usePost();
  const { mutateAsync: update, isPending: isUpdating } = PushQuery.usePut();
  const { mutateAsync: send, isPending: isSending } = PushQuery.useSend();
  const { mutateAsync: schedule, isPending: isScheduling } =
    PushQuery.useSchedule();

  const isBusy = isCreating || isUpdating || isSending || isScheduling;
  // 임시 저장은 status 를 되돌리지 않아 예약 건에서는 뜻이 없다.
  const isScheduled = push?.status === 'SCHEDULED';

  useEffect(() => {
    if (!push) return;

    setPushType(push.pushType);
    setTargetType(push.targetType);
    setSelectedUsers(
      (push.targetUsers ?? []).map(({ id, name }) => ({ id, name, phone: '' })),
    );
    setTitle(push.title);
    setBody(push.body);
    setDestinationType(push.destinationType);
    setDestinationDetail(push.destinationDetail);
    setSendMethod(push.status === 'SCHEDULED' ? 'SCHEDULE' : 'NOW');
    setSendAt(push.sendAt ? dayjs(push.sendAt).toDate() : null);
  }, [push]);

  const validate = () => {
    if (!title.trim()) return '제목을 입력해주세요.';
    if (!body.trim()) return '내용을 입력해주세요.';
    if (targetType === 'INDIVIDUAL' && selectedUsers.length === 0) {
      return '발송할 회원을 선택해주세요.';
    }
    if (
      DESTINATIONS_WITH_DETAIL.includes(destinationType) &&
      !destinationDetail
    ) {
      return destinationType === 'EVENT_DETAIL'
        ? '연결할 봉사를 선택해주세요.'
        : '연결 대상을 입력해주세요.';
    }
    if (sendMethod === 'SCHEDULE' && !sendAt) {
      return '예약 일시를 지정해주세요.';
    }
    return null;
  };

  /** 저장(신규면 생성, 수정이면 갱신)하고 대상 id 를 돌려준다. */
  const persist = async (): Promise<number> => {
    const payload = {
      title: title.trim(),
      body: body.trim(),
      pushType,
      targetType,
      ...(targetType === 'INDIVIDUAL'
        ? { userIds: selectedUsers.map((u) => u.id) }
        : {}),
      destinationType,
      ...(destinationDetail ? { destinationDetail } : {}),
    };

    if (push) {
      await update({ id: push.id, ...payload });
      return push.id;
    }

    const { data } = await create(payload);
    return data.id as number;
  };

  const handleSaveDraft = async () => {
    const message = validate();
    if (message) {
      toast.error(message);
      return;
    }

    try {
      const id = await persist();
      toast.success('임시 저장되었습니다.');
      navigate(`${pushUrl}/${id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSubmit = () => {
    const message = validate();
    if (message) {
      toast.error(message);
      return;
    }

    const targetLabel =
      targetType === 'ALL'
        ? '전체 회원'
        : `선택한 회원 ${selectedUsers.length.toLocaleString('ko')}명`;

    handleConfirm({
      confirmContent:
        sendMethod === 'NOW' ? (
          <>
            <p>{targetLabel}에게 푸시를 발송하시겠습니까?</p>
            <p css={styles.confirmNote}>
              발송을 시작한 후에는 내용을 수정하거나 취소할 수 없습니다.
            </p>
          </>
        ) : (
          <p>
            {dayjs(sendAt).format('YYYY.MM.DD HH시 mm분')}에 {targetLabel}에게
            푸시를 발송합니다. 예약하시겠습니까?
          </p>
        ),
      yesTitle: sendMethod === 'NOW' ? '발송' : '예약',
      noTitle: '취소',
      confirmNext: () => void submit(),
    });
  };

  const submit = async () => {
    try {
      const id = await persist();

      if (sendMethod === 'NOW') {
        await send(id);
        toast.success('푸시를 발송했습니다.');
      } else {
        await schedule({
          id,
          sendAt: dayjs(sendAt).format('YYYY-MM-DD HH:mm:ss'),
        });
        toast.success('푸시 발송을 예약했습니다.');
      }

      navigate(`${pushUrl}/${id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleReset = () => {
    setPushType('NOTICE');
    setTargetType('ALL');
    setSelectedUsers([]);
    setTitle('');
    setBody('');
    setDestinationType('TAB_HOME');
    setSelectedEvent(null);
    setSelectedContent(null);
    setDestinationDetail(null);
    setSendMethod('NOW');
    setSendAt(null);
  };

  const handleSelectEvent = (event: EventItemType) => {
    setSelectedEvent(event);
    setDestinationDetail(String(event.id));
    setIsEventModalOpen(false);
  };

  const handleSelectContent = (content: IContent) => {
    setSelectedContent(content);
    setDestinationDetail(String(content.id));
    setIsContentModalOpen(false);
  };

  return (
    <div css={styles.wrap}>
      <div css={styles.formBox}>
        <UiComponent.Box>
          <div css={styles.fields}>
            <div css={styles.field}>
              <p css={styles.label}>푸시 유형</p>
              <FormComponent.Select
                css={{ width: '200px' }}
                options={PUSH_TYPE_OPTIONS}
                value={pushType}
                handleChange={setPushType}
              />
            </div>

            <div css={styles.field}>
              <p css={styles.label}>발송 대상</p>
              <div
                css={{ display: 'flex', alignItems: 'center', gap: '0 8px' }}
              >
                <FormComponent.Select
                  css={{ width: '200px' }}
                  options={PUSH_TARGET_OPTIONS}
                  value={targetType}
                  handleChange={setTargetType}
                />
                {targetType === 'INDIVIDUAL' && (
                  <>
                    <UiComponent.Button
                      variant="border"
                      color="primary"
                      css={{ height: '34px' }}
                      onClick={() => setIsUserModalOpen(true)}
                    >
                      회원 선택
                    </UiComponent.Button>
                    <span css={styles.selectedEvent}>
                      {selectedUsers.length
                        ? `${selectedUsers.length.toLocaleString('ko')}명 선택`
                        : '선택된 회원 없음'}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div css={styles.field}>
              <p css={styles.label}>제목</p>
              <FormComponent.MaxInput
                css={{ width: '360px' }}
                maxLength={PUSH_TITLE_MAX_LENGTH}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div css={styles.field}>
              <p css={styles.label}>내용</p>
              <FormComponent.MaxTextarea
                css={{ width: '360px', height: '80px' }}
                maxLength={PUSH_BODY_MAX_LENGTH}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>

            <div css={styles.field}>
              <p css={styles.label}>연결 화면</p>
              <div
                css={{ display: 'flex', alignItems: 'center', gap: '0 8px' }}
              >
                <FormComponent.Select
                  css={{ width: '200px' }}
                  options={PUSH_DESTINATION_OPTIONS}
                  value={destinationType}
                  handleChange={(value) => {
                    setDestinationType(value);
                    setSelectedEvent(null);
                    setSelectedContent(null);
                    setDestinationDetail(null);
                  }}
                />
                {destinationType === 'EVENT_DETAIL' && (
                  <>
                    <UiComponent.Button
                      variant="border"
                      color="primary"
                      css={{ height: '34px' }}
                      onClick={() => setIsEventModalOpen(true)}
                    >
                      봉사 선택
                    </UiComponent.Button>
                    <span css={styles.selectedEvent}>
                      {selectedEvent
                        ? `#${selectedEvent.id} ${selectedEvent.title}`
                        : destinationDetail
                          ? `#${destinationDetail}`
                          : '선택된 봉사 없음'}
                    </span>
                  </>
                )}
                {destinationType === 'CONTENTS' && (
                  <>
                    <UiComponent.Button
                      variant="border"
                      color="primary"
                      css={{ height: '34px' }}
                      onClick={() => setIsContentModalOpen(true)}
                    >
                      컨텐츠 선택
                    </UiComponent.Button>
                    <span css={styles.selectedEvent}>
                      {selectedContent
                        ? `#${selectedContent.id} ${selectedContent.title}`
                        : destinationDetail
                          ? `#${destinationDetail}`
                          : '선택된 컨텐츠 없음'}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div css={styles.field}>
              <p css={styles.label}>발송 방식</p>
              <div css={styles.radios}>
                <FormComponent.Radio
                  name="sendMethod"
                  value="NOW"
                  current={sendMethod}
                  handleClick={setSendMethod}
                >
                  즉시 발송
                </FormComponent.Radio>
                <FormComponent.Radio
                  name="sendMethod"
                  value="SCHEDULE"
                  current={sendMethod}
                  handleClick={setSendMethod}
                >
                  예약 발송
                </FormComponent.Radio>
              </div>
            </div>

            {sendMethod === 'SCHEDULE' && (
              <div css={styles.field}>
                <p css={styles.label}>예약 일시</p>
                <div>
                  <FormComponent.DatePicker
                    date={sendAt}
                    handleChange={setSendAt}
                    minDate={new Date()}
                    showTimeSelect
                    timeIntervals={SCHEDULE_MINUTE_UNIT}
                    dateFormat="yyyy.MM.dd HH:mm"
                    placeholderText="예약 일시 선택"
                  />
                  <p css={styles.hint}>
                    {SCHEDULE_MINUTE_UNIT >= 60
                      ? '정시 단위로만 예약할 수 있습니다.'
                      : `${SCHEDULE_MINUTE_UNIT}분 단위로 예약할 수 있습니다.`}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div css={styles.buttons}>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={handleReset}
            >
              초기화
            </UiComponent.Button>
            {!isScheduled && (
              <UiComponent.Button
                variant="border"
                color="primary"
                onClick={handleSaveDraft}
                disabled={isBusy}
              >
                임시 저장
              </UiComponent.Button>
            )}
            <UiComponent.Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isBusy}
            >
              {sendMethod === 'NOW'
                ? '즉시 발송'
                : isScheduled
                  ? '예약 변경'
                  : '발송 예약'}
            </UiComponent.Button>
          </div>
        </UiComponent.Box>
      </div>

      <div css={styles.previewBox}>
        <UiComponent.Box>
          <Preview
            title={title}
            body={body}
            targetCount={
              targetType === 'ALL' ? targetCount : selectedUsers.length
            }
          />
        </UiComponent.Box>
      </div>

      {isEventModalOpen && (
        <EventSelect
          onSelect={handleSelectEvent}
          onClose={() => setIsEventModalOpen(false)}
        />
      )}
      {isUserModalOpen && (
        <UserSelect
          selected={selectedUsers}
          onSelect={(users) => {
            setSelectedUsers(users);
            setIsUserModalOpen(false);
          }}
          onClose={() => setIsUserModalOpen(false)}
        />
      )}
      {isContentModalOpen && (
        <ContentSelect
          onSelect={handleSelectContent}
          onClose={() => setIsContentModalOpen(false)}
        />
      )}
    </div>
  );
}

export default Form;
